// v4.1 - 5 tabs update
// Didi99FoodV4Page - 99Food OpenDelivery v4 Admin
import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { 
  Settings, 
  ShoppingBag, 
  Activity,
  TestTube, 
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  EyeOff,
  Clock,
  Webhook,
  Server,
  Database,
  RotateCcw,
  Loader2,
  Trash2,
  Plus,
  Copy,
  ArrowRightLeft,
  Store,
  Power,
  Building2,
  ClipboardCheck,
  ExternalLink,
  Tag,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import Didi99FoodDiagnostic from '../components/Didi99FoodDiagnostic';

import { MainLayout } from '../components/Layout';
const API_V4_PREFIX = '/v4/99food';

// Helper to parse and format moreInfo JSON
const formatMoreInfo = (moreInfo) => {
  if (!moreInfo) return null;
  
  try {
    const data = typeof moreInfo === 'string' ? JSON.parse(moreInfo) : moreInfo;
    if (!Array.isArray(data)) return { raw: moreInfo };
    
    return data.map(item => ({
      operation: item.operationType,
      success: item.successList?.length || 0,
      failed: item.failedList?.length || 0,
      warnings: item.warningList?.length || 0,
      errors: item.failedList?.map(f => {
        try {
          const msg = JSON.parse(f.message || '[]');
          return msg.map(m => m.errmsg || m.reason).join(', ');
        } catch {
          return f.message || 'Erro desconhecido';
        }
      }) || []
    }));
  } catch {
    return { raw: moreInfo };
  }
};

// Checklist items with API endpoints
const CHECKLIST_ITEMS = [
  { 
    module: 'Pedido', 
    action: 'Confirmar Pedido', 
    endpoint: '/orders/{orderId}/confirm',
    method: 'POST',
    description: 'Aceita um pedido recebido'
  },
  { 
    module: 'Pedido', 
    action: 'Cancelar Pedido', 
    endpoint: '/orders/{orderId}/requestCancellation',
    method: 'POST',
    description: 'Solicita cancelamento de um pedido'
  },
  { 
    module: 'Pedido', 
    action: 'Informar Pedido Pronto', 
    endpoint: '/orders/{orderId}/readyForPickup',
    method: 'POST',
    description: 'Marca pedido como pronto para retirada'
  },
  { 
    module: 'Pedido', 
    action: 'Informar Pedido Entregue', 
    endpoint: '/orders/{orderId}/delivered',
    method: 'POST',
    description: 'Marca pedido como entregue (self-delivery)'
  },
  { 
    module: 'Pedido', 
    action: 'Receber Pagamento Online', 
    endpoint: 'Webhook: CREATED',
    method: 'WEBHOOK',
    description: 'Pedido pago online (payment_pending = 0)'
  },
  { 
    module: 'Pedido', 
    action: 'Receber Pagamento em Dinheiro', 
    endpoint: 'Webhook: CREATED',
    method: 'WEBHOOK',
    description: 'Pedido com dinheiro (payment_pending > 0)'
  },
  { 
    module: 'Pedido', 
    action: 'Finalizar com Entrega da 99', 
    endpoint: 'Webhook: DELIVERED',
    method: 'WEBHOOK',
    description: 'Entrega realizada pela 99 (delivery_mode = 99DELIVERY)'
  },
  { 
    module: 'Pedido', 
    action: 'Finalizar com Entrega da Loja', 
    endpoint: '/orders/{orderId}/delivered',
    method: 'POST',
    description: 'Entrega própria (delivery_mode = SELF_DELIVERY)'
  },
  { 
    module: 'Pedido', 
    action: 'Verificar Valor dos Itens', 
    endpoint: '/orders/{orderId}',
    method: 'GET',
    description: 'Campo: total.itemsPrice'
  },
  { 
    module: 'Pedido', 
    action: 'Verificar Valor da Entrega', 
    endpoint: '/orders/{orderId}',
    method: 'GET',
    description: 'Campo: total.deliveryFee'
  },
  { 
    module: 'Pedido', 
    action: 'Verificar Descontos', 
    endpoint: '/orders/{orderId}',
    method: 'GET',
    description: 'Campo: total.discount'
  },
  { 
    module: 'Pedido', 
    action: 'Verificar Quanto já foi pago', 
    endpoint: '/orders/{orderId}',
    method: 'GET',
    description: 'Campo: payments.prepaid'
  },
  { 
    module: 'Pedido', 
    action: 'Verificar Quanto falta pagar', 
    endpoint: '/orders/{orderId}',
    method: 'GET',
    description: 'Campo: payments.pending'
  },
  { 
    module: 'Pedido', 
    action: 'Verificar Nome do cliente', 
    endpoint: '/orders/{orderId}',
    method: 'GET',
    description: 'Campo: customer.name'
  },
  { 
    module: 'Pedido', 
    action: 'Verificar Endereço do cliente', 
    endpoint: '/orders/{orderId}',
    method: 'GET',
    description: 'Campo: delivery.deliveryAddress'
  },
  { 
    module: 'Pedido', 
    action: 'Verificar Número do pedido (order_index)', 
    endpoint: '/orders/{orderId}',
    method: 'GET',
    description: 'Campo: displayId ou orderIndex'
  },
];

const Didi99FoodV4Page = () => {
  const [activeTab, setActiveTab] = useState('config');
  const [loading, setLoading] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [showSecrets, setShowSecrets] = useState(false);
  
  // Connection state
  const [connection, setConnection] = useState(null);
  const [connectionId, setConnectionId] = useState(null);
  
  // Integration status
  const [status, setStatus] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    client_id: '',
    client_secret: '',
    app_id: '',
    merchant_id: '',
    mode: 'BOTH',
    webhook_secret: '',
    polling_enabled: true,
    polling_interval_seconds: 30,
    // Sync options
    sync_dispatch_to_od: true,
    sync_delivery_to_od: true,
    driver_identification: 'PHONE',
    sync_concluded_from_od: true,
    concluded_status: 'ENTREGUE',
    sync_cancelled_from_od: true,
    integration_mode: 'AUTO_CONFIRM',
    estimated_commission_rate: ''
  });
  
  // Events
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  
  // Logs
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logFilter, setLogFilter] = useState({ type: "", status: "", endpoint: "" });
  const [selectedLog, setSelectedLog] = useState(null);
  const [showLogModal, setShowLogModal] = useState(false);
  
  // Orders
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);

  // Merchant/Store
  const [merchantStatus, setMerchantStatus] = useState(null);
  const [merchantLoading, setMerchantLoading] = useState(false);
  const [merchantStatusLoading, setMerchantStatusLoading] = useState(false);

  // Menu Sync
  const [menuPreview, setMenuPreview] = useState(null);
  const [menuPreviewLoading, setMenuPreviewLoading] = useState(false);
  const [menuSyncLoading, setMenuSyncLoading] = useState(false);

  // API Tests
  const [testingAuth, setTestingAuth] = useState(false);
  const [testingPolling, setTestingPolling] = useState(false);
  const [testingOrder, setTestingOrder] = useState(false);
  const [testingMerchant, setTestingMerchant] = useState(false);
  const [authTestResult, setAuthTestResult] = useState(null);
  const [pollingTestResult, setPollingTestResult] = useState(null);
  const [orderTestResult, setOrderTestResult] = useState(null);
  const [merchantTestResult, setMerchantTestResult] = useState(null);
  const [orderIdToTest, setOrderIdToTest] = useState('');

  // Homologacao states
  const [homologOrderId, setHomologOrderId] = useState('');
  const [homologOrderDetails, setHomologOrderDetails] = useState(null);
  const [homologTestLoading, setHomologTestLoading] = useState(null);
  const [homologTestResults, setHomologTestResults] = useState({});
  const [selectedCancelCode, setSelectedCancelCode] = useState("SYSTEMIC_ISSUES");

  // Load status on mount
  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      setLoading(true);
      const response = await api.get(`${API_V4_PREFIX}/admin/status`);
      setStatus(response.data);
      
      if (response.data.configured && response.data.connection) {
        setConnectionId(response.data.connection.id);
        await loadConnectionDetails(response.data.connection.id);
      }
    } catch (error) {
      console.error('Error loading status:', error);
      toast.error('Erro ao carregar status da integração');
    } finally {
      setLoading(false);
    }
  };

  const loadConnectionDetails = async (connId) => {
    try {
      const response = await api.get(`${API_V4_PREFIX}/connections/${connId}`);
      setConnection(response.data);
      setFormData({
        client_id: response.data.client_id || '',
        client_secret: '', // Never returned from API
        app_id: response.data.app_id || '',
        merchant_id: response.data.merchant_id || '',
        mode: response.data.mode || 'BOTH',
        webhook_secret: '', // Never returned from API
        polling_enabled: response.data.polling_enabled ?? true,
        polling_interval_seconds: response.data.polling_interval_seconds || 30,
        // Sync options
        sync_dispatch_to_od: response.data.sync_dispatch_to_od ?? true,
        sync_delivery_to_od: response.data.sync_delivery_to_od ?? true,
        driver_identification: response.data.driver_identification || 'PHONE',
        sync_concluded_from_od: response.data.sync_concluded_from_od ?? true,
        concluded_status: response.data.concluded_status || 'ENTREGUE',
        sync_cancelled_from_od: response.data.sync_cancelled_from_od ?? true,
        integration_mode: response.data.integration_mode || 'AUTO_CONFIRM',
        estimated_commission_rate: response.data.estimated_commission_rate != null ? String(response.data.estimated_commission_rate) : ''
      });
    } catch (error) {
      console.error('Error loading connection:', error);
    }
  };

  const handleSaveConnection = async () => {
    try {
      setLoading(true);
      
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      if (connectionId) {
        // Update existing connection
        const updateData = {
          client_id: formData.client_id,
          app_id: formData.app_id,
          merchant_id: formData.merchant_id,
          mode: formData.mode,
          polling_enabled: formData.polling_enabled,
          polling_interval_seconds: formData.polling_interval_seconds,
          // Sync options
          sync_dispatch_to_od: formData.sync_dispatch_to_od,
          sync_delivery_to_od: formData.sync_delivery_to_od,
          driver_identification: formData.driver_identification,
          sync_concluded_from_od: formData.sync_concluded_from_od,
          concluded_status: formData.concluded_status,
          sync_cancelled_from_od: formData.sync_cancelled_from_od,
          integration_mode: formData.integration_mode
        };
        
        // Taxa de comissão estimada (APENAS VISUAL)
        if (formData.estimated_commission_rate !== '' && formData.estimated_commission_rate !== null) {
          const rate = parseFloat(formData.estimated_commission_rate);
          if (!isNaN(rate) && rate >= 0 && rate <= 100) {
            updateData.estimated_commission_rate = rate;
          }
        }
        
        // Only include secrets if provided
        if (formData.client_secret) {
          updateData.client_secret = formData.client_secret;
        }
        if (formData.webhook_secret) {
          updateData.webhook_secret = formData.webhook_secret;
        }
        
        await api.patch(`${API_V4_PREFIX}/connections/${connectionId}`, updateData);
        toast.success('Configuração atualizada com sucesso');
      } else {
        // Create new connection
        if (!formData.client_id || !formData.client_secret || !formData.app_id || !formData.merchant_id) {
          toast.error('Preencha todos os campos obrigatórios');
          return;
        }
        
        const response = await api.post(`${API_V4_PREFIX}/connections`, {
          store_id: user.store_id,
          client_id: formData.client_id,
          client_secret: formData.client_secret,
          app_id: formData.app_id,
          merchant_id: formData.merchant_id,
          mode: formData.mode,
          webhook_secret: formData.webhook_secret || null
        });
        
        setConnectionId(response.data.id);
        toast.success('Conexão criada com sucesso');
      }
      
      await loadStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar configuração');
    } finally {
      setLoading(false);
    }
  };

  const handleTestAuth = async () => {
    if (!connectionId) {
      toast.error('Salve a configuração primeiro');
      return;
    }
    
    try {
      setLoading(true);
      const response = await api.post(`${API_V4_PREFIX}/connections/${connectionId}/auth/test`);
      
      if (response.data.success) {
        toast.success('Autenticação bem-sucedida!');
        await loadStatus();
      } else {
        toast.error(response.data.message || 'Falha na autenticação');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao testar autenticação');
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshToken = async () => {
    if (!connectionId) return;
    
    try {
      setLoading(true);
      await api.post(`${API_V4_PREFIX}/connections/${connectionId}/auth/refresh`);
      toast.success('Token atualizado com sucesso');
      await loadStatus();
    } catch (error) {
      toast.error('Erro ao atualizar token');
    } finally {
      setLoading(false);
    }
  };

  const loadEvents = async () => {
    if (!connectionId) return;
    
    try {
      setEventsLoading(true);
      const response = await api.get(`${API_V4_PREFIX}/admin/events?connection_id=${connectionId}&limit=50`);
      setEvents(response.data.events || []);
    } catch (error) {
      console.error('Error loading events:', error);
      toast.error('Erro ao carregar eventos');
    } finally {
      setEventsLoading(false);
    }
  };

  const loadLogs = async () => {
    if (!connectionId) return;
    
    try {
      setLogsLoading(true);
      const response = await api.get(`${API_V4_PREFIX}/admin/logs?connection_id=${connectionId}&limit=50`);
      setLogs(response.data.logs || []);
    } catch (error) {
      console.error('Error loading logs:', error);
      toast.error('Erro ao carregar logs');
    } finally {
      setLogsLoading(false);
    }
  };

  const loadOrders = async () => {
    if (!connectionId) return;
    
    try {
      setOrdersLoading(true);
      const response = await api.get(`${API_V4_PREFIX}/connections/${connectionId}/orders?limit=50`);
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error('Error loading orders:', error);
      toast.error('Erro ao carregar pedidos');
    } finally {
      setOrdersLoading(false);
    }
  };

  // Merchant/Store functions
  const loadMerchantStatus = async () => {
    if (!connectionId) return;
    
    try {
      setMerchantLoading(true);
      const response = await api.get(`${API_V4_PREFIX}/connections/${connectionId}/merchant/status`);
      setMerchantStatus(response.data);
    } catch (error) {
      console.error('Error loading merchant status:', error);
      toast.error('Erro ao carregar status da loja');
    } finally {
      setMerchantLoading(false);
    }
  };

  const handleSetMerchantStatus = async (status) => {
    if (!connectionId) return;
    
    try {
      setMerchantStatusLoading(true);
      const response = await api.post(`${API_V4_PREFIX}/connections/${connectionId}/merchant/status`, {
        status: status
      });
      
      if (response.data.success) {
        toast.success(`Loja ${status === 'AVAILABLE' ? 'aberta' : 'fechada'} com sucesso!`);
        await loadMerchantStatus();
      } else {
        toast.error(response.data.error || 'Erro ao atualizar status');
      }
    } catch (error) {
      console.error('Error setting merchant status:', error);
      toast.error('Erro ao atualizar status da loja');
    } finally {
      setMerchantStatusLoading(false);
    }
  };

  // Menu Sync functions
  const loadMenuPreview = async () => {
    if (!connectionId) return;
    
    try {
      setMenuPreviewLoading(true);
      const response = await api.get(`${API_V4_PREFIX}/connections/${connectionId}/menu/preview`);
      setMenuPreview(response.data);
    } catch (error) {
      console.error('Error loading menu preview:', error);
      toast.error('Erro ao carregar prévia do cardápio');
    } finally {
      setMenuPreviewLoading(false);
    }
  };

  const handleSyncMenu = async () => {
    if (!connectionId) return;
    
    try {
      setMenuSyncLoading(true);
      const response = await api.post(`${API_V4_PREFIX}/connections/${connectionId}/menu/sync`);
      
      if (response.data.success) {
        toast.success(response.data.message || 'Cardápio sincronizado com sucesso!');
        await loadMerchantStatus();
      } else {
        toast.error(response.data.error || 'Erro ao sincronizar cardápio');
      }
    } catch (error) {
      console.error('Error syncing menu:', error);
      toast.error('Erro ao sincronizar cardápio');
    } finally {
      setMenuSyncLoading(false);
    }
  };

  const handleRetryEvent = async (eventId) => {
    try {
      await api.post(`${API_V4_PREFIX}/admin/events/${eventId}/retry`);
      toast.success('Evento reenfileirado para processamento');
      await loadEvents();
    } catch (error) {
      toast.error('Erro ao reprocessar evento');
    }
  };

  // Checklist API Test functions
  const handleChecklistTestAuth = async () => {
    try {
      setTestingAuth(true);
      setAuthTestResult(null);
      const response = await api.post(`${API_V4_PREFIX}/test/auth`);
      setAuthTestResult(response.data);
    } catch (error) {
      setAuthTestResult({ success: false, error: error.message });
    } finally {
      setTestingAuth(false);
    }
  };

  const handleChecklistTestPolling = async () => {
    try {
      setTestingPolling(true);
      setPollingTestResult(null);
      const response = await api.post(`${API_V4_PREFIX}/test/polling`);
      setPollingTestResult(response.data);
    } catch (error) {
      setPollingTestResult({ success: false, error: error.message });
    } finally {
      setTestingPolling(false);
    }
  };

  const handleChecklistTestOrder = async () => {
    if (!orderIdToTest.trim()) {
      toast.error('Digite o ID do pedido');
      return;
    }
    try {
      setTestingOrder(true);
      setOrderTestResult(null);
      const response = await api.post(`${API_V4_PREFIX}/test/order/${orderIdToTest}`);
      setOrderTestResult(response.data);
    } catch (error) {
      setOrderTestResult({ success: false, error: error.message });
    } finally {
      setTestingOrder(false);
    }
  };

  const handleChecklistTestMerchant = async () => {
    try {
      setTestingMerchant(true);
      setMerchantTestResult(null);
      const response = await api.post(`${API_V4_PREFIX}/test/merchant-status`);
      setMerchantTestResult(response.data);
    } catch (error) {
      setMerchantTestResult({ success: false, error: error.message });
    } finally {
      setTestingMerchant(false);
    }
  };


  // Homologacao functions
  const loadHomologOrderDetails = async (orderId) => {
    if (!connectionId || !orderId) return;
    try {
      const response = await api.get(`${API_V4_PREFIX}/connections/${connectionId}/orders/${orderId}`);
      const data = response.data;
      // Flatten the nested structure for easier rendering
      const flattened = {
        ...data,
        // Flatten customer
        customer_name: data.customer?.name || data.customer_name,
        customer_phone: data.customer?.phone || data.customer_phone,
        // Flatten totals
        subtotal: data.totals?.subtotal ?? data.subtotal ?? 0,
        delivery_fee: data.totals?.delivery_fee ?? data.delivery_fee ?? 0,
        discount: data.totals?.discount ?? data.discount ?? 0,
        total: data.totals?.total ?? data.total ?? 0,
        // Flatten payment
        payment_method: data.payment?.method || data.payment_method,
        payment_prepaid: data.payment?.prepaid ?? data.payment_prepaid ?? 0,
        payment_pending: data.payment?.pending ?? data.payment_pending ?? 0,
        // Flatten delivery address
        delivery_address_street: data.delivery_address?.street || data.delivery_address_street,
        delivery_address_number: data.delivery_address?.number || data.delivery_address_number,
        delivery_address_complement: data.delivery_address?.complement || data.delivery_address_complement,
        delivery_address_neighborhood: data.delivery_address?.neighborhood || data.delivery_address_neighborhood,
        delivery_address_city: data.delivery_address?.city || data.delivery_address_city,
        delivery_address_state: data.delivery_address?.state || data.delivery_address_state,
        delivery_address_postal_code: data.delivery_address?.postal_code || data.delivery_address_postal_code,
        // Flatten cancellation
        cancellation_reason: data.cancellation?.reason || data.cancellation_reason,
        cancellation_code: data.cancellation?.code || data.cancellation_code,
        cancellation_requested_by: data.cancellation?.requested_by || data.cancellation_requested_by,
        cancellation_at: data.cancellation?.cancelled_at || data.cancellation_at,
        // Coupon/Discount details (v4.3)
        totals: data.totals || {},
        coupon: data.coupon || {},
        delivery_mode_label: data.delivery_mode_label || (data.delivery_mode === "MERCHANT" ? "LOJA" : "99FOOD"),
      };
      setHomologOrderDetails(flattened);
    } catch (error) {
      console.error('Error loading order details:', error);
      toast.error('Erro ao carregar detalhes do pedido');
    }
  };

  const executeHomologTest = async (testType) => {
    if (!connectionId || !homologOrderId) {
      toast.error('Selecione um pedido primeiro');
      return;
    }
    try {
      setHomologTestLoading(testType);
      let endpoint = '';
      switch (testType) {
        case 'confirm': endpoint = `/connections/${connectionId}/orders/${homologOrderId}/confirm`; break;
        case 'cancel': endpoint = `/connections/${connectionId}/orders/${homologOrderId}/cancel`; break;
        case 'ready': endpoint = `/connections/${connectionId}/orders/${homologOrderId}/ready`; break;
        case 'dispatch': endpoint = `/connections/${connectionId}/orders/${homologOrderId}/dispatch`; break;
        case 'delivered': endpoint = `/connections/${connectionId}/orders/${homologOrderId}/delivered`; break;
        case 'accept-cancellation': endpoint = `/connections/${connectionId}/orders/${homologOrderId}/accept-cancellation`; break;
        case 'deny-cancellation': endpoint = `/connections/${connectionId}/orders/${homologOrderId}/deny-cancellation`; break;
        default: return;
      }
      let requestBody = {};
      if (testType === 'cancel') {
        requestBody = { reason: 'Teste de homologação - cancelamento', code: selectedCancelCode, mode: 'MANUAL' };
      }
      const response = await api.post(`${API_V4_PREFIX}${endpoint}`, requestBody);
      setHomologTestResults(prev => ({ ...prev, [testType]: { success: true, message: response.data.message || 'OK' } }));
      toast.success(`Teste "${testType}" executado com sucesso!`);
      await loadHomologOrderDetails(homologOrderId);
      await loadOrders();
    } catch (error) {
      setHomologTestResults(prev => ({ ...prev, [testType]: { success: false, error: error.response?.data?.detail || error.message } }));
      toast.error(`Erro no teste: ${error.response?.data?.detail || error.message}`);
    } finally {
      setHomologTestLoading(null);
    }
  };
  const handleDeleteConnection = async () => {
    if (!connectionId) return;
    
    if (!window.confirm('Tem certeza que deseja excluir esta conexão? Esta ação não pode ser desfeita.')) {
      return;
    }
    
    try {
      setLoading(true);
      await api.delete(`${API_V4_PREFIX}/connections/${connectionId}`);
      toast.success('Conexão excluída com sucesso');
      setConnection(null);
      setConnectionId(null);
      setFormData({
        client_id: '',
        client_secret: '',
        app_id: '',
        merchant_id: '',
        mode: 'BOTH',
        webhook_secret: '',
        polling_enabled: true,
        polling_interval_seconds: 30
      });
      await loadStatus();
    } catch (error) {
      toast.error('Erro ao excluir conexão');
    } finally {
      setLoading(false);
    }
  };

  const copyWebhookUrl = () => {
    const webhookUrl = `${window.location.origin}/api/v4/99food/webhook`;
    navigator.clipboard.writeText(webhookUrl);
    toast.success('URL do webhook copiada!');
  };

  const getStatusBadge = (eventStatus) => {
    const statusConfig = {
      PENDING: { color: 'bg-yellow-500', text: 'Pendente' },
      PROCESSING: { color: 'bg-blue-500', text: 'Processando' },
      ACKED: { color: 'bg-green-500', text: 'Processado' },
      FAILED: { color: 'bg-red-500', text: 'Falhou' },
      DEAD: { color: 'bg-gray-500', text: 'Descartado' }
    };
    const config = statusConfig[eventStatus] || { color: 'bg-gray-400', text: eventStatus };
    return <Badge className={`${config.color} text-white`}>{config.text}</Badge>;
  };

  const getOrderStatusBadge = (orderStatus) => {
    const statusConfig = {
      PLACED: { color: 'bg-yellow-500', text: 'Novo' },
      CONFIRMED: { color: 'bg-blue-500', text: 'Confirmado' },
      READY_FOR_PICKUP: { color: 'bg-purple-500', text: 'Pronto' },
      DISPATCHED: { color: 'bg-orange-500', text: 'Saiu' },
      DELIVERED: { color: 'bg-green-500', text: 'Entregue' },
      CONCLUDED: { color: 'bg-green-600', text: 'Concluído' },
      CANCELLED: { color: 'bg-red-500', text: 'Cancelado' }
    };
    const config = statusConfig[orderStatus] || { color: 'bg-gray-400', text: orderStatus };
    return <Badge className={`${config.color} text-white`}>{config.text}</Badge>;
  };

  return (

    <MainLayout>
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6" data-testid="didi99food-v4-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
        <div>
          <h1 className="text-lg sm:text-2xl font-bold text-gray-900">99Food OpenDelivery v4</h1>
          <p className="text-gray-500">Configuração e monitoramento da integração</p>
        </div>
        <div className="flex items-center gap-1 text-xs px-2 py-1.5 whitespace-nowrap">
          {status?.worker?.running ? (
            <Badge className="bg-green-500 text-white gap-1">
              <Server className="w-3 h-3" /> Worker Ativo
            </Badge>
          ) : (
            <Badge className="bg-red-500 text-white gap-1">
              <Server className="w-3 h-3" /> Worker Inativo
            </Badge>
          )}
          <Button variant="outline" size="sm" onClick={loadStatus} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <p className="text-lg sm:text-2xl font-bold">
                  {status?.configured ? (
                    status?.connection?.is_authenticated ? (
                      <span className="text-green-600">Conectado</span>
                    ) : (
                      <span className="text-yellow-600">Não Autenticado</span>
                    )
                  ) : (
                    <span className="text-gray-400">Não Configurado</span>
                  )}
                </p>
              </div>
              {status?.connection?.is_authenticated ? (
                <CheckCircle className="w-8 h-8 text-green-500" />
              ) : (
                <AlertCircle className="w-8 h-8 text-yellow-500" />
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
              <div>
                <p className="text-sm text-gray-500">Modo</p>
                <p className="text-lg sm:text-2xl font-bold">{status?.connection?.mode || '-'}</p>
              </div>
              <Webhook className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
              <div>
                <p className="text-sm text-gray-500">Eventos Pendentes</p>
                <p className="text-lg sm:text-2xl font-bold">{status?.queue?.pending || 0}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
              <div>
                <p className="text-sm text-gray-500">Eventos com Falha</p>
                <p className="text-2xl font-bold text-red-600">{status?.queue?.failed || 0}</p>
              </div>
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex overflow-x-auto gap-1 w-full pb-1">
          <TabsTrigger value="config" className="flex items-center gap-1 text-xs px-2 py-1.5 whitespace-nowrap">
            <Settings className="w-4 h-4" /><span className="hidden sm:inline ml-1">Config</span>
          </TabsTrigger>
          <TabsTrigger value="sync" className="flex items-center gap-1 text-xs px-2 py-1.5 whitespace-nowrap">
            <ArrowRightLeft className="w-4 h-4" /><span className="hidden sm:inline ml-1">Sync</span>
          </TabsTrigger>
          <TabsTrigger value="store" className="flex items-center gap-1 text-xs px-2 py-1.5 whitespace-nowrap">
          <TabsTrigger value="menu" className="flex items-center gap-1 text-xs px-2 py-1.5 whitespace-nowrap">
            <Database className="w-3 h-3" /> Menu
          </TabsTrigger>
            <Store className="w-4 h-4" /><span className="hidden sm:inline ml-1">Loja</span>
          </TabsTrigger>
          <TabsTrigger value="checklist" className="flex items-center gap-1 text-xs px-2 py-1.5 whitespace-nowrap">
            <ClipboardCheck className="w-4 h-4" /><span className="hidden sm:inline ml-1">Homolog</span>
          </TabsTrigger>
          <TabsTrigger value="events" className="flex items-center gap-2" onClick={loadEvents}>
            <Activity className="w-4 h-4" /><span className="hidden sm:inline ml-1">Eventos</span>
          </TabsTrigger>
          <TabsTrigger value="orders" className="flex items-center gap-2" onClick={loadOrders}>
            <ShoppingBag className="w-4 h-4" /><span className="hidden sm:inline ml-1">Pedidos</span>
          </TabsTrigger>
          <TabsTrigger value="logs" className="flex items-center gap-2" onClick={loadLogs}>
            <Database className="w-4 h-4" /><span className="hidden sm:inline ml-1">Logs</span>
          </TabsTrigger>
          <TabsTrigger value="diagnostic" className="flex items-center gap-2">
            <Activity className="w-4 h-4" /><span className="hidden sm:inline ml-1">Diagnóstico</span>
          </TabsTrigger>
        </TabsList>

        {/* Config Tab */}
        <TabsContent value="config">
          <Card>
            <CardHeader>
              <CardTitle>Credenciais OpenDelivery v4</CardTitle>
              <CardDescription>
                Configure as credenciais da API 99Food OpenDelivery v4
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Webhook URL */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                  <div>
                    <p className="font-medium text-blue-900">URL do Webhook</p>
                    <p className="text-xs sm:text-sm text-blue-700 font-mono break-all">
                      {window.location.origin}/api/v4/99food/webhook
                    </p>
                  </div>
                  <Button variant="outline" size="sm" onClick={copyWebhookUrl}>
                    <Copy className="w-4 h-4 mr-2" /> Copiar
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="client_id">Client ID (AppID_AppShopId) *</Label>
                  <Input
                    id="client_id"
                    placeholder="Ex: ABC123_SHOP456"
                    value={formData.client_id}
                    onChange={(e) => setFormData({...formData, client_id: e.target.value})}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="client_secret">Client Secret *</Label>
                  <div className="relative">
                    <Input
                      id="client_secret"
                      type={showSecrets ? "text" : "password"}
                      placeholder={connectionId ? "••••••• (deixe vazio para manter)" : "Insira o secret"}
                      value={formData.client_secret}
                      onChange={(e) => setFormData({...formData, client_secret: e.target.value})}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-2 top-1/2 -translate-y-1/2"
                      onClick={() => setShowSecrets(!showSecrets)}
                    >
                      {showSecrets ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="app_id">App ID *</Label>
                  <Input
                    id="app_id"
                    placeholder="ID do aplicativo"
                    value={formData.app_id}
                    onChange={(e) => setFormData({...formData, app_id: e.target.value})}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="merchant_id">Merchant ID *</Label>
                  <Input
                    id="merchant_id"
                    placeholder="ID do estabelecimento"
                    value={formData.merchant_id}
                    onChange={(e) => setFormData({...formData, merchant_id: e.target.value})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="mode">Modo de Recebimento</Label>
                  <select
                    id="mode"
                    className="w-full p-2 border rounded-md"
                    value={formData.mode}
                    onChange={(e) => setFormData({...formData, mode: e.target.value})}
                  >
                    <option value="WEBHOOK">Webhook (Recomendado)</option>
                    <option value="POLLING">Polling</option>
                    <option value="BOTH">Ambos</option>
                  </select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="webhook_secret">Webhook Secret (HMAC)</Label>
                  <Input
                    id="webhook_secret"
                    type={showSecrets ? "text" : "password"}
                    placeholder={connectionId ? "••••••• (deixe vazio para manter)" : "Secret para validação HMAC"}
                    value={formData.webhook_secret}
                    onChange={(e) => setFormData({...formData, webhook_secret: e.target.value})}
                  />
                </div>
              </div>

              {(formData.mode === 'POLLING' || formData.mode === 'BOTH') && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="flex items-center gap-4">
                    <Switch
                      id="polling_enabled"
                      checked={formData.polling_enabled}
                      onCheckedChange={(checked) => setFormData({...formData, polling_enabled: checked})}
                    />
                    <Label htmlFor="polling_enabled">Polling Habilitado</Label>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="polling_interval">Intervalo de Polling (segundos)</Label>
                    <Input
                      id="polling_interval"
                      type="number"
                      min="10"
                      max="300"
                      value={formData.polling_interval_seconds}
                      onChange={(e) => setFormData({...formData, polling_interval_seconds: parseInt(e.target.value)})}
                    />
                  </div>
                </div>
              )}

              {/* Taxa Estimada de Comissao 99Food */}
              {connectionId && (
                <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg space-y-2" data-testid="99food-commission-config">
                  <Label htmlFor="estimated_commission_rate" className="text-orange-800 font-semibold text-sm">
                    Taxa estimada de comissao 99Food (%)
                  </Label>
                  <div className="flex items-center gap-3">
                    <Input
                      id="estimated_commission_rate"
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      placeholder="Ex.: 3,21"
                      value={formData.estimated_commission_rate}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || (parseFloat(val) >= 0 && parseFloat(val) <= 100)) {
                          setFormData({...formData, estimated_commission_rate: val});
                        }
                      }}
                      className="max-w-[160px]"
                      data-testid="99food-commission-rate-input"
                    />
                    <span className="text-xs text-orange-600">%</span>
                  </div>
                  <p className="text-[11px] text-orange-500 italic">
                    Usada apenas para estimar o repasse no modal de pedidos. Nao altera o financeiro oficial.
                  </p>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2 sm:gap-3 pt-4 border-t">
                <Button onClick={handleSaveConnection} disabled={loading}>
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  {connectionId ? 'Atualizar Configuração' : 'Criar Conexão'}
                </Button>
                
                {connectionId && (
                  <>
                    <Button variant="outline" onClick={handleTestAuth} disabled={loading}>
                      <TestTube className="w-4 h-4 mr-2" />
                      Testar Autenticação
                    </Button>
                    
                    <Button variant="outline" onClick={handleRefreshToken} disabled={loading}>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Renovar Token
                    </Button>
                    
                    <Button variant="destructive" onClick={handleDeleteConnection} disabled={loading}>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Excluir
                    </Button>
                  </>
                )}
              </div>

              {/* Connection Info */}
              {connection && (
                <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium mb-3 text-sm sm:text-base">Informações da Conexão</h4>
                  <div className="grid grid-cols-1 gap-3 text-xs sm:text-sm">
                    <div className="flex flex-col gap-1">
                      <span className="text-gray-500 text-xs">ID:</span>
                      <span className="font-mono text-xs break-all bg-white p-1 rounded">{connection.id}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex flex-col gap-1">
                        <span className="text-gray-500 text-xs">Autenticado:</span>
                        <span className={connection.is_authenticated ? 'text-green-600 font-semibold' : 'text-red-600'}>{connection.is_authenticated ? 'Sim' : 'Não'}</span>
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className="text-gray-500 text-xs">Última Auth:</span>
                        <span className="text-xs">{connection.last_auth_at ? new Date(connection.last_auth_at).toLocaleString('pt-BR') : 'Nunca'}</span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-gray-500 text-xs">Último Evento:</span>
                      <span className="text-xs">{connection.last_event_at ? new Date(connection.last_event_at).toLocaleString('pt-BR') : 'Nunca'}</span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sync Tab */}
        <TabsContent value="sync">
          <Card>
            <CardHeader>
              <CardTitle>Sincronização</CardTitle>
              <CardDescription>
                Configure a sincronização entre o Sistema e o Open Delivery
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Sistema → Open Delivery */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-medium text-gray-800 mb-4 flex items-center gap-2 text-lg">
                  <span className="text-blue-600 font-semibold">Sistema</span> 
                  <ArrowRightLeft className="w-5 h-5 text-gray-400" />
                  <span className="text-orange-600 font-semibold">Open Delivery</span>
                </h4>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                    <Label htmlFor="sync_dispatch" className="font-medium">Sincronizar despacho dos pedidos</Label>
                    <Switch
                      id="sync_dispatch"
                      checked={formData.sync_dispatch_to_od}
                      onCheckedChange={(checked) => setFormData({...formData, sync_dispatch_to_od: checked})}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                    <Label htmlFor="sync_delivery" className="font-medium">Sincronizar entrega dos pedidos</Label>
                    <Switch
                      id="sync_delivery"
                      checked={formData.sync_delivery_to_od}
                      onCheckedChange={(checked) => setFormData({...formData, sync_delivery_to_od: checked})}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                    <Label htmlFor="driver_id" className="font-medium">Identificação do entregador</Label>
                    <select
                      id="driver_id"
                      className="p-2 border rounded-md w-48"
                      value={formData.driver_identification}
                      onChange={(e) => setFormData({...formData, driver_identification: e.target.value})}
                    >
                      <option value="PHONE">Telefone</option>
                      <option value="NAME">Nome</option>
                      <option value="PLATE">Placa</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Open Delivery → Sistema */}
              <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                <h4 className="font-medium text-gray-800 mb-4 flex items-center gap-2 text-lg">
                  <span className="text-orange-600 font-semibold">Open Delivery</span> 
                  <ArrowRightLeft className="w-5 h-5 text-gray-400" />
                  <span className="text-blue-600 font-semibold">Sistema</span>
                </h4>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                    <div>
                      <Label htmlFor="sync_concluded" className="font-medium">Sincronizar pedidos concluídos para:</Label>
                    </div>
                    <div className="flex items-center gap-1 text-xs px-2 py-1.5 whitespace-nowrap">
                      <select
                        className="p-2 border rounded-md w-40"
                        value={formData.concluded_status}
                        onChange={(e) => setFormData({...formData, concluded_status: e.target.value})}
                      >
                        <option value="ENTREGUE">Entregue</option>
                        <option value="FINALIZADO">Finalizado</option>
                      </select>
                      <Switch
                        id="sync_concluded"
                        checked={formData.sync_concluded_from_od}
                        onCheckedChange={(checked) => setFormData({...formData, sync_concluded_from_od: checked})}
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                    <Label htmlFor="sync_cancelled" className="font-medium">Sincronizar pedidos cancelados</Label>
                    <Switch
                      id="sync_cancelled"
                      checked={formData.sync_cancelled_from_od}
                      onCheckedChange={(checked) => setFormData({...formData, sync_cancelled_from_od: checked})}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                    <div>
                      <Label htmlFor="integration_mode" className="font-medium">Integrar:</Label>
                      <p className="text-sm text-gray-500">Modo de recebimento de pedidos</p>
                    </div>
                    <select
                      id="integration_mode"
                      className="p-2 border rounded-md w-64"
                      value={formData.integration_mode}
                      onChange={(e) => setFormData({...formData, integration_mode: e.target.value})}
                    >
                      <option value="AUTO_CONFIRM">Pedidos pendentes e confirmá-los</option>
                      <option value="CONFIRMED_ONLY">Apenas pedidos confirmados</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-end pt-4">
                <Button onClick={handleSaveConnection} disabled={loading || !connectionId}>
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  Salvar Configurações
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Store Tab */}
        <TabsContent value="store">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-1 text-xs px-2 py-1.5 whitespace-nowrap">
                <Building2 className="w-5 h-5" />
                Gerenciamento da Loja
              </CardTitle>
              <CardDescription>
                Gerencie o status e informações da sua loja no 99Food
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Store Status */}
              <div className="p-4 bg-gray-50 rounded-lg border">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-medium text-lg flex items-center gap-2">
                    <Power className="w-5 h-5" />
                    Status da Loja
                  </h4>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={loadMerchantStatus}
                    disabled={merchantLoading || !connectionId}
                  >
                    {merchantLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                
                {merchantStatus ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                      <div className="p-3 bg-white rounded-lg border">
                        <p className="text-sm text-gray-500">Status</p>
                        <Badge className={
                          merchantStatus.data?.status === 'SUCCESS' 
                            ? 'bg-green-100 text-green-800' 
                            : merchantStatus.data?.status === 'PROCESSING'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }>
                          {merchantStatus.data?.status || 'N/A'}
                        </Badge>
                      </div>
                      <div className="p-3 bg-white rounded-lg border">
                        <p className="text-sm text-gray-500">Última Leitura</p>
                        <p className="font-medium">
                          {merchantStatus.data?.lastRead 
                            ? new Date(merchantStatus.data.lastRead).toLocaleString('pt-BR')
                            : 'N/A'}
                        </p>
                      </div>
                    </div>
                    
                    {/* Formatted moreInfo */}
                    {merchantStatus.data?.moreInfo && (
                      <div className="mt-4 p-3 bg-gray-50 rounded-lg border">
                        <p className="text-sm text-gray-500 mb-2">Detalhes da Sincronização</p>
                        <div className="space-y-2">
                          {(() => {
                            const parsed = formatMoreInfo(merchantStatus.data.moreInfo);
                            if (parsed?.raw) {
                              return <p className="text-xs text-gray-600 break-words">{parsed.raw}</p>;
                            }
                            return parsed?.map((op, idx) => (
                              <div key={idx} className="flex items-center gap-2 text-xs">
                                <span className="font-mono bg-gray-200 px-2 py-1 rounded">{op.operation}</span>
                                {op.success > 0 && <Badge className="bg-green-100 text-green-800">{op.success} OK</Badge>}
                                {op.failed > 0 && <Badge className="bg-red-100 text-red-800">{op.failed} Erro</Badge>}
                                {op.errors?.length > 0 && (
                                  <span className="text-red-600 truncate max-w-[300px]" title={op.errors.join(', ')}>
                                    {op.errors[0]}
                                  </span>
                                )}
                              </div>
                            ));
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500">
                    <p>Clique em atualizar para verificar o status da loja</p>
                  </div>
                )}
              </div>

              {/* Open/Close Store */}
              <div className="p-4 bg-gray-50 rounded-lg border">
                <h4 className="font-medium text-lg mb-4 flex items-center gap-2">
                  <Store className="w-5 h-5" />
                  Abrir/Fechar Loja
                </h4>
                <p className="text-sm text-gray-500 mb-4">
                  Use esses botões para abrir ou fechar sua loja no 99Food em caso de emergência.
                  <br/>
                  <span className="text-orange-600 font-medium">
                    Atenção: Use apenas para eventos inesperados. Não use para feriados ou horários programados.
                  </span>
                </p>
                
                <div className="flex gap-4">
                  <Button
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    onClick={() => handleSetMerchantStatus('AVAILABLE')}
                    disabled={merchantStatusLoading || !connectionId}
                  >
                    {merchantStatusLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4 mr-2" />
                    )}
                    Abrir Loja
                  </Button>
                  <Button
                    className="flex-1 bg-red-600 hover:bg-red-700"
                    onClick={() => handleSetMerchantStatus('UNAVAILABLE')}
                    disabled={merchantStatusLoading || !connectionId}
                  >
                    {merchantStatusLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <XCircle className="w-4 h-4 mr-2" />
                    )}
                    Fechar Loja
                  </Button>
                </div>
              </div>

              {/* Info Box */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-medium text-blue-900 mb-2">Informações</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• <strong>Merchant Status:</strong> Verifica o status da integração com o 99Food</li>
                  <li>• <strong>Abrir/Fechar:</strong> Altera a disponibilidade da loja em caso de emergência</li>
                  <li>• <strong>Cardápio:</strong> Gerencie pelo B-App do 99Food</li>
                  <li>• <strong>Pedidos:</strong> Recebidos automaticamente via OpenDelivery</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        {/* Menu Tab */}
        <TabsContent value="menu">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="w-5 h-5" />
                Sincronizar Cardápio
              </CardTitle>
              <CardDescription>
                Envie seu cardápio para a 99Food
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <Button 
                  className="bg-green-600 hover:bg-green-700"
                  onClick={async () => {
                    if (!connectionId) return;
                    try {
                      setLoading(true);
                      setSyncResult(null);
                      const response = await api.post(`${API_V4_PREFIX}/connections/${connectionId}/menu/sync`);
                      const data = response.data;
                      setSyncResult(data);
                      if (data.success) {
                        toast.success(data.message || 'Cardápio sincronizado com sucesso!');
                      } else {
                        toast.error(data.error || 'Erro na sincronização');
                      }
                    } catch (error) {
                      const errorMsg = error.response?.data?.error || error.response?.data?.detail || 'Erro ao sincronizar cardápio';
                      toast.error(errorMsg);
                      setSyncResult({ success: false, error: errorMsg });
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading || !connectionId}
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Sincronizar Menu com 99Food
                </Button>
                <Button variant="outline" onClick={() => window.open('/categories', '_blank')}>
                  <Tag className="w-4 h-4 mr-2" />
                  Gerenciar Categorias
                </Button>
              </div>
              <p className="text-sm text-gray-500">
                Isso enviará todos os produtos, categorias e variações para a 99Food.
              </p>
              
              {syncResult && (
                <div className={`mt-4 p-4 rounded-lg border ${syncResult.success ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
                  <div className="flex items-start gap-3">
                    {syncResult.success ? <CheckCircle className="w-5 h-5 text-green-600" /> : <XCircle className="w-5 h-5 text-red-600" />}
                    <div>
                      <p className={`font-medium ${syncResult.success ? "text-green-800" : "text-red-800"}`}>
                        {syncResult.success ? "Sincronização concluída!" : "Falha na sincronização"}
                      </p>
                      {syncResult.error && <p className="text-sm text-red-700 mt-1">{syncResult.error}</p>}
                      {syncResult.stats && (
                        <p className="text-sm text-slate-600 mt-1">
                          {syncResult.stats.categories || 0} categorias | {syncResult.stats.products || 0} produtos
                          {syncResult.stats.invalid_images > 0 && <span className="text-amber-600"> | {syncResult.stats.invalid_images} imagens inválidas</span>}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Checklist Tab */}
        <TabsContent value="checklist">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg sm:text-xl flex items-center gap-2">
                <ClipboardCheck className="w-5 h-5 text-orange-500" />
                Homologação 99Food
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                Teste cada funcionalidade exigida pela 99Food para homologação
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Selecionar Pedido para Teste */}
              <div className="p-3 sm:p-4 bg-orange-50 rounded-lg border border-orange-200">
                <h4 className="font-semibold text-orange-800 mb-3 text-sm sm:text-base">1. Selecione um Pedido para Testar</h4>
                <div className="flex gap-2">
                  <select
                    className="flex-1 p-2 border rounded-md bg-white text-sm"
                    value={homologOrderId}
                    onChange={(e) => {
                      setHomologOrderId(e.target.value);
                      if (e.target.value) {
                        loadHomologOrderDetails(e.target.value);
                      } else {
                        setHomologOrderDetails(null);
                      }
                    }}
                  >
                    <option value="">-- Selecione um pedido --</option>
                    {orders.map(order => (
                      <option key={order.order_id} value={order.order_id}>
                        #{order.display_id || order.order_id.slice(0, 8)} - {order.customer_name} - {order.status}
                      </option>
                    ))}
                  </select>
                  <Button variant="outline" onClick={loadOrders} disabled={ordersLoading}>
                    <RefreshCw className={`w-4 h-4 ${ordersLoading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
                {!orders.length && (
                  <p className="text-xs sm:text-sm text-orange-600 mt-2">
                    Nenhum pedido encontrado. Crie um pedido no C-App do 99Food primeiro.
                  </p>
                )}
              </div>

              {/* Detalhes do Pedido Selecionado */}
              {homologOrderDetails && (
                <div className="p-3 sm:p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-blue-800 mb-3 text-sm sm:text-base">Dados do Pedido Selecionado</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 text-xs sm:text-sm">
                    <div><span className="text-gray-500">Número:</span> <span className="font-medium">#{homologOrderDetails.display_id || homologOrderDetails.order_id?.slice(0, 8)}</span></div>
                    <div><span className="text-gray-500">Cliente:</span> <span className="font-medium">{homologOrderDetails.customer_name}</span></div>
                    <div><span className="text-gray-500">Status:</span> {getOrderStatusBadge(homologOrderDetails.status)}</div>
                    <div><span className="text-gray-500">Subtotal:</span> <span className="font-medium">R$ {(homologOrderDetails.subtotal || 0).toFixed(2)}</span></div>
                    <div><span className="text-gray-500">Taxa Entrega:</span> <span className="font-medium">R$ {(homologOrderDetails.delivery_fee || 0).toFixed(2)}</span></div>
                    <div><span className="text-gray-500">Desconto Total:</span> <span className="font-medium">R$ {(homologOrderDetails.discount || 0).toFixed(2)}</span></div>
                    
                    {/* CUPOM/DESCONTO DETALHADO */}
                    {(homologOrderDetails.totals?.discount_merchant > 0 || homologOrderDetails.totals?.discount_platform > 0 || homologOrderDetails.coupon?.source) && (
                      <div className="col-span-2 sm:col-span-3 p-2 bg-yellow-50 rounded border border-yellow-200">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-yellow-800 font-medium">Cupom:</span>
                          <Badge className={homologOrderDetails.coupon?.source === "LOJA" ? "bg-blue-600" : homologOrderDetails.coupon?.source === "99FOOD" ? "bg-orange-500" : "bg-purple-500"}>
                            {homologOrderDetails.coupon?.source || "N/A"}
                          </Badge>
                        </div>
                        <div className="text-xs space-y-1">
                          {homologOrderDetails.totals?.discount_merchant > 0 && (
                            <div><span className="text-blue-600 font-medium">Desconto (LOJA):</span> R$ {homologOrderDetails.totals.discount_merchant.toFixed(2)}</div>
                          )}
                          {homologOrderDetails.totals?.discount_platform > 0 && (
                            <div><span className="text-orange-600 font-medium">Desconto (99FOOD):</span> R$ {homologOrderDetails.totals.discount_platform.toFixed(2)}</div>
                          )}
                          {homologOrderDetails.coupon?.name && (
                            <div><span className="text-gray-500">Promocao:</span> {homologOrderDetails.coupon.name}</div>
                          )}
                        </div>
                      </div>
                    )}
                    <div><span className="text-gray-500">Total:</span> <span className="font-bold text-green-600">R$ {(homologOrderDetails.total || 0).toFixed(2)}</span></div>
                    <div><span className="text-gray-500">Pago Online:</span> <span className="font-medium">{homologOrderDetails.payment_prepaid ? 'Sim' : 'Não'}</span></div>
                    <div><span className="text-gray-500">A Pagar:</span> <span className="font-medium text-orange-600">R$ {(homologOrderDetails.payment_pending || 0).toFixed(2)}</span></div>
                    <div><span className="text-gray-500">Tipo Pagamento:</span> <span className="font-medium text-blue-600">{homologOrderDetails.payment?.method || "N/A"}</span></div>
                    <div><span className="text-gray-500">Já Pago:</span> <span className="font-medium text-green-600">R$ {(homologOrderDetails.payment?.amount_paid || 0).toFixed(2)}</span></div>
                    <div><span className="text-gray-500">Nº Pedido 99:</span> <span className="font-medium text-purple-600">{homologOrderDetails.order_index || "N/A"}</span></div>
                    <div className="col-span-2 sm:col-span-3">
                      <span className="text-gray-500">Observações:</span>
                      <span className="font-medium ml-1 break-words text-purple-700">
                        {homologOrderDetails.notes || 'Nenhuma'}
                      </span>
                    </div>
                    <div className="col-span-2 sm:col-span-3">
                      <span className="text-gray-500">Endereço:</span>
                      <span className="font-medium ml-1 break-words">
                        {homologOrderDetails.delivery_address_street}{homologOrderDetails.delivery_address_number ? `, ${homologOrderDetails.delivery_address_number}` : ''} - {homologOrderDetails.delivery_address_neighborhood}, {homologOrderDetails.delivery_address_city}
                      </span>
                    </div>
                    <div className="col-span-2 sm:col-span-3">
                    <div className="col-span-2 sm:col-span-3">
                      <span className="text-gray-500">Entrega:</span>
                      <Badge className={`ml-2 ${homologOrderDetails.delivery_mode_label === "LOJA" || homologOrderDetails.delivery_mode === "MERCHANT" ? "bg-blue-600" : "bg-orange-500"}`}>
                        {homologOrderDetails.delivery_mode_label || (homologOrderDetails.delivery_mode === "MERCHANT" ? "LOJA" : "99FOOD")}
                      </Badge>
                      <span className="text-xs text-gray-400 ml-2">
                        ({(homologOrderDetails.delivery_mode_label === "LOJA" || homologOrderDetails.delivery_mode === "MERCHANT") ? "Motoboy proprio" : "Logistica 99"})
                      </span>
                    </div>
                    </div>
                    {homologOrderDetails.status === 'CANCELLED' && (
                      <div className="col-span-2 sm:col-span-3 mt-2 p-2 bg-red-50 rounded border border-red-200">
                        <span className="text-red-600 font-medium">Cancelamento:</span>
                        <div className="text-xs mt-1">
                          {homologOrderDetails.cancellation_code && <p><strong>Código:</strong> {homologOrderDetails.cancellation_code}</p>}
                          {homologOrderDetails.cancellation_reason && <p><strong>Motivo:</strong> {homologOrderDetails.cancellation_reason}</p>}
                          {homologOrderDetails.cancellation_requested_by && <p><strong>Solicitado por:</strong> {homologOrderDetails.cancellation_requested_by}</p>}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Testes de Homologação */}
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-800 text-base sm:text-lg border-b pb-2">2. Testes de Homologação</h4>
                
                {/* AÇÕES NO PEDIDO */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {/* Confirmar Pedido */}
                  <div className="p-3 sm:p-4 bg-white rounded-lg border-2 border-gray-200 hover:border-green-300 transition-colors">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
                      <h5 className="font-medium text-sm">Confirmar Pedido</h5>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">Aceita um pedido recebido</p>
                    <Button 
                      onClick={() => executeHomologTest('confirm')} 
                      disabled={!homologOrderId || homologTestLoading === 'confirm'}
                      className="w-full bg-green-500 hover:bg-green-600 text-xs sm:text-sm"
                      size="sm"
                    >
                      {homologTestLoading === 'confirm' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Testar Confirmar
                    </Button>
                    {homologTestResults.confirm && (
                      <div className={`mt-2 p-2 rounded text-xs ${homologTestResults.confirm.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {homologTestResults.confirm.success ? '✓ OK' : `✗ ${homologTestResults.confirm.error}`}
                      </div>
                    )}
                  </div>

                  {/* Cancelar Pedido */}
                  <div className="p-3 sm:p-4 bg-white rounded-lg border-2 border-gray-200 hover:border-red-300 transition-colors">
                    <div className="flex items-center gap-2 mb-2">
                      <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
                      <h5 className="font-medium text-sm">Cancelar Pedido</h5>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">Solicita cancelamento</p>
                    <select
                      value={selectedCancelCode}
                      onChange={(e) => setSelectedCancelCode(e.target.value)}
                      className="w-full mb-2 p-2 text-xs border rounded"
                    >
                      <option value="SYSTEMIC_ISSUES">Problemas de sistema</option>
                      <option value="DUPLICATE_APPLICATION">Pedido duplicado</option>
                      <option value="UNAVAILABLE_ITEM">Item indisponível</option>
                      <option value="OUT_OF_STOCK">Sem estoque</option>
                      <option value="RESTAURANT_CLOSED">Restaurante fechado</option>
                    </select>
                    <Button 
                      onClick={() => executeHomologTest('cancel')} 
                      disabled={!homologOrderId || homologTestLoading === 'cancel'}
                      className="w-full bg-red-500 hover:bg-red-600 text-xs sm:text-sm"
                      size="sm"
                    >
                      {homologTestLoading === 'cancel' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Testar Cancelar
                    </Button>
                    {homologTestResults.cancel && (
                      <div className={`mt-2 p-2 rounded text-xs ${homologTestResults.cancel.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {homologTestResults.cancel.success ? '✓ OK' : `✗ ${homologTestResults.cancel.error}`}
                      </div>
                    )}
                  </div>

                  {/* Receber Cancelamento */}
                  <div className="p-3 sm:p-4 bg-white rounded-lg border-2 border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500" />
                      <h5 className="font-medium text-sm">Receber Cancelamento</h5>
                    </div>
                    <p className="text-xs text-gray-500 mb-2">Via webhook CANCELLED</p>
                    <Badge className="bg-purple-100 text-purple-800 text-xs">WEBHOOK</Badge>
                    {homologOrderDetails && homologOrderDetails.status === 'CANCELLED' ? (
                      <div className="mt-2 p-2 rounded text-xs bg-green-100 text-green-800">
                        <p>✓ Pedido cancelado recebido!</p>
                        {homologOrderDetails.cancellation_code && (
                          <p className="mt-1"><strong>Código:</strong> {homologOrderDetails.cancellation_code}</p>
                        )}
                        {homologOrderDetails.cancellation_reason && (
                          <p><strong>Motivo:</strong> {homologOrderDetails.cancellation_reason}</p>
                        )}
                        {homologOrderDetails.cancellation_requested_by && (
                          <p><strong>Solicitado por:</strong> {homologOrderDetails.cancellation_requested_by}</p>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 mt-2">Cancele pelo C-App para testar</p>
                    )}
                  </div>


                  {/* Aceitar Cancelamento */}
                  <div className="p-3 sm:p-4 bg-white rounded-lg border-2 border-gray-200 hover:border-green-300 transition-colors">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
                      <h5 className="font-medium text-sm">Aceitar Cancelamento</h5>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">Aceita solicitação de cancelamento</p>
                    <Button
                      onClick={() => executeHomologTest("accept-cancellation")}
                      disabled={!homologOrderId || homologTestLoading === "accept-cancellation"}
                      className="w-full bg-green-500 hover:bg-green-600 text-xs sm:text-sm"
                      size="sm"
                    >
                      {homologTestLoading === "accept-cancellation" && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Aceitar Cancelamento
                    </Button>
                    {homologTestResults["accept-cancellation"] && (
                      <div className={`mt-2 p-2 rounded text-xs ${homologTestResults["accept-cancellation"].success ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                        {homologTestResults["accept-cancellation"].success ? "✓ OK" : `✗ ${homologTestResults["accept-cancellation"].error}`}
                      </div>
                    )}
                  </div>

                  {/* Negar Cancelamento */}
                  <div className="p-3 sm:p-4 bg-white rounded-lg border-2 border-gray-200 hover:border-red-300 transition-colors">
                    <div className="flex items-center gap-2 mb-2">
                      <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
                      <h5 className="font-medium text-sm">Negar Cancelamento</h5>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">Nega solicitação de cancelamento</p>
                    <Button
                      onClick={() => executeHomologTest("deny-cancellation")}
                      disabled={!homologOrderId || homologTestLoading === "deny-cancellation"}
                      className="w-full bg-orange-500 hover:bg-orange-600 text-xs sm:text-sm"
                      size="sm"
                    >
                      {homologTestLoading === "deny-cancellation" && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Negar Cancelamento
                    </Button>
                    {homologTestResults["deny-cancellation"] && (
                      <div className={`mt-2 p-2 rounded text-xs ${homologTestResults["deny-cancellation"].success ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                        {homologTestResults["deny-cancellation"].success ? "✓ OK" : `✗ ${homologTestResults["deny-cancellation"].error}`}
                      </div>
                    )}
                  </div>
                  {/* Informar Pedido Pronto */}
                  <div className="p-3 sm:p-4 bg-white rounded-lg border-2 border-gray-200 hover:border-blue-300 transition-colors">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
                      <h5 className="font-medium text-sm">Informar Pedido Pronto</h5>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">Marca como pronto para retirada</p>
                    <Button 
                      onClick={() => executeHomologTest('ready')} 
                      disabled={!homologOrderId || homologTestLoading === 'ready'}
                      className="w-full bg-blue-500 hover:bg-blue-600 text-xs sm:text-sm"
                      size="sm"
                    >
                      {homologTestLoading === 'ready' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Testar Pronto
                    </Button>
                    {homologTestResults.ready && (
                      <div className={`mt-2 p-2 rounded text-xs ${homologTestResults.ready.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {homologTestResults.ready.success ? '✓ OK' : `✗ ${homologTestResults.ready.error}`}
                      </div>
                    )}
                  </div>

                  {/* Informar Pedido Entregue */}
                  <div className="p-3 sm:p-4 bg-white rounded-lg border-2 border-gray-200 hover:border-green-300 transition-colors">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                      <h5 className="font-medium text-sm">Informar Pedido Entregue</h5>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">Marca como entregue (self-delivery)</p>
                    <Button 
                      onClick={() => executeHomologTest('delivered')} 
                      disabled={!homologOrderId || homologTestLoading === 'delivered'}
                      className="w-full bg-green-600 hover:bg-green-700 text-xs sm:text-sm"
                      size="sm"
                    >
                      {homologTestLoading === 'delivered' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Testar Entregue
                    </Button>
                    {homologTestResults.delivered && (
                      <div className={`mt-2 p-2 rounded text-xs ${homologTestResults.delivered.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {homologTestResults.delivered.success ? '✓ OK' : `✗ ${homologTestResults.delivered.error}`}
                      </div>
                    )}
                  </div>

                  {/* Dispatch */}
                  <div className="p-3 sm:p-4 bg-white rounded-lg border-2 border-gray-200 hover:border-orange-300 transition-colors">
                    <div className="flex items-center gap-2 mb-2">
                      <ArrowRightLeft className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500" />
                      <h5 className="font-medium text-sm">Despachar Pedido</h5>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">Saiu para entrega (dispatch)</p>
                    <Button 
                      onClick={() => executeHomologTest('dispatch')} 
                      disabled={!homologOrderId || homologTestLoading === 'dispatch'}
                      className="w-full bg-orange-500 hover:bg-orange-600 text-xs sm:text-sm"
                      size="sm"
                    >
                      {homologTestLoading === 'dispatch' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Testar Dispatch
                    </Button>
                    {homologTestResults.dispatch && (
                      <div className={`mt-2 p-2 rounded text-xs ${homologTestResults.dispatch.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {homologTestResults.dispatch.success ? '✓ OK' : `✗ ${homologTestResults.dispatch.error}`}
                      </div>
                    )}
                  </div>
                </div>

                {/* TIPOS DE PAGAMENTO */}
                <h4 className="font-semibold text-gray-800 text-sm sm:text-md border-b pb-2 mt-6">Tipos de Pagamento</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="p-3 sm:p-4 bg-white rounded-lg border-2 border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
                      <h5 className="font-medium text-sm">Receber Pagamento Online</h5>
                    </div>
                    <p className="text-xs text-gray-500 mb-2">Pedido pago online (payment_pending = 0)</p>
                    <Badge className="bg-purple-100 text-purple-800 text-xs">WEBHOOK: CREATED</Badge>
                    {homologOrderDetails && (
                      <div className={`mt-2 p-2 rounded text-xs ${(homologOrderDetails.payment_pending || 0) === 0 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {(homologOrderDetails.payment_pending || 0) === 0 ? '✓ Este pedido foi pago online' : '⚠ Este pedido NÃO foi pago online'}
                      </div>
                    )}
                  </div>

                  <div className="p-3 sm:p-4 bg-white rounded-lg border-2 border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500" />
                      <h5 className="font-medium text-sm">Receber Pagamento em Dinheiro</h5>
                    </div>
                    <p className="text-xs text-gray-500 mb-2">Pedido com dinheiro (payment_pending &gt; 0)</p>
                    <Badge className="bg-purple-100 text-purple-800 text-xs">WEBHOOK: CREATED</Badge>
                    {homologOrderDetails && (
                      <div className={`mt-2 p-2 rounded text-xs ${(homologOrderDetails.payment_pending || 0) > 0 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {(homologOrderDetails.payment_pending || 0) > 0 ? `✓ Este pedido tem R$ ${(homologOrderDetails.payment_pending || 0).toFixed(2)} a receber` : '⚠ Este pedido foi pago online'}
                      </div>
                    )}
                  </div>
                </div>

                {/* TIPOS DE ENTREGA */}
                <h4 className="font-semibold text-gray-800 text-sm sm:text-md border-b pb-2 mt-6">Tipos de Entrega</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="p-3 sm:p-4 bg-white rounded-lg border-2 border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Building2 className="w-4 h-4 sm:w-5 sm:h-5 text-purple-500" />
                      <h5 className="font-medium text-sm">Finalizar com Entrega da 99</h5>
                    </div>
                    <p className="text-xs text-gray-500 mb-2">delivery_mode = 99DELIVERY</p>
                    <Badge className="bg-purple-100 text-purple-800 text-xs">WEBHOOK: DELIVERED</Badge>
                    {homologOrderDetails && (
                      <div className={`mt-2 p-2 rounded text-xs ${homologOrderDetails.delivery_mode === '99DELIVERY' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {homologOrderDetails.delivery_mode === '99DELIVERY' ? '✓ Este pedido é entrega da 99' : '⚠ Este pedido é entrega própria'}
                      </div>
                    )}
                  </div>

                  <div className="p-3 sm:p-4 bg-white rounded-lg border-2 border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Store className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
                      <h5 className="font-medium text-sm">Finalizar com Entrega da Loja</h5>
                    </div>
                    <p className="text-xs text-gray-500 mb-2">delivery_mode = SELF_DELIVERY</p>
                    <Badge className="bg-blue-100 text-blue-800 text-xs">POST /delivered</Badge>
                    {homologOrderDetails && (
                      <div className={`mt-2 p-2 rounded text-xs ${homologOrderDetails.delivery_mode !== '99DELIVERY' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {homologOrderDetails.delivery_mode !== '99DELIVERY' ? '✓ Este pedido é entrega própria' : '⚠ Este pedido é entrega da 99'}
                      </div>
                    )}
                  </div>
                </div>

                {/* VERIFICAÇÕES DE DADOS */}
                <h4 className="font-semibold text-gray-800 text-sm sm:text-md border-b pb-2 mt-6">Verificações de Dados</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
                  <div className="p-2 sm:p-3 bg-gray-50 rounded-lg border">
                    <h5 className="font-medium text-xs mb-1">Valor dos Itens</h5>
                    <p className="text-base sm:text-lg font-bold text-gray-800">{homologOrderDetails ? `R$ ${(homologOrderDetails.subtotal || 0).toFixed(2)}` : '-'}</p>
                    {homologOrderDetails && <Badge className="bg-green-100 text-green-800 text-xs">✓</Badge>}
                  </div>
                  <div className="p-2 sm:p-3 bg-gray-50 rounded-lg border">
                    <h5 className="font-medium text-xs mb-1">Valor Entrega</h5>
                    <p className="text-base sm:text-lg font-bold text-gray-800">{homologOrderDetails ? `R$ ${(homologOrderDetails.delivery_fee || 0).toFixed(2)}` : '-'}</p>
                    {homologOrderDetails && <Badge className="bg-green-100 text-green-800 text-xs">✓</Badge>}
                  </div>
                  <div className="p-2 sm:p-3 bg-gray-50 rounded-lg border">
                    <h5 className="font-medium text-xs mb-1">Descontos</h5>
                    <p className="text-base sm:text-lg font-bold text-gray-800">{homologOrderDetails ? `R$ ${(homologOrderDetails.discount || 0).toFixed(2)}` : '-'}</p>
                    {homologOrderDetails && <Badge className="bg-green-100 text-green-800 text-xs">✓</Badge>}
                  </div>
                  <div className="p-2 sm:p-3 bg-gray-50 rounded-lg border">
                    <h5 className="font-medium text-xs mb-1">Nome Cliente</h5>
                    <p className="text-xs sm:text-sm font-bold text-gray-800 truncate">{homologOrderDetails?.customer_name || '-'}</p>
                    {homologOrderDetails && <Badge className="bg-green-100 text-green-800 text-xs">✓</Badge>}
                  </div>
                  <div className="p-2 sm:p-3 bg-gray-50 rounded-lg border">
                    <h5 className="font-medium text-xs mb-1">Nº Pedido</h5>
                    <p className="text-base sm:text-lg font-bold text-gray-800">#{homologOrderDetails?.display_id || homologOrderDetails?.order_id?.slice(0, 6) || '-'}</p>
                    {homologOrderDetails && <Badge className="bg-green-100 text-green-800 text-xs">✓</Badge>}
                  </div>
                  <div className="p-2 sm:p-3 bg-gray-50 rounded-lg border">
                    <h5 className="font-medium text-xs mb-1">Observações</h5>
                    <p className="text-xs text-gray-600 truncate">{homologOrderDetails?.notes || 'Nenhuma'}</p>
                    {homologOrderDetails && <Badge className="bg-green-100 text-green-800 text-xs">✓</Badge>}
                  </div>
                </div>
                <div className="p-2 sm:p-3 bg-gray-50 rounded-lg border">
                  <h5 className="font-medium text-xs mb-1">Endereço do Cliente</h5>
                  <p className="text-xs sm:text-sm text-gray-800 break-words">
                    {homologOrderDetails ? `${homologOrderDetails.delivery_address_street}${homologOrderDetails.delivery_address_number ? `, ${homologOrderDetails.delivery_address_number}` : ''} - ${homologOrderDetails.delivery_address_neighborhood}, ${homologOrderDetails.delivery_address_city}` : '-'}
                  </p>
                  {homologOrderDetails && <Badge className="bg-green-100 text-green-800 text-xs mt-1">✓ Verificado</Badge>}
                </div>
              </div>

              {/* Resumo */}
              <div className="p-3 sm:p-4 bg-green-50 rounded-lg border border-green-200 mt-6">
                <h4 className="font-semibold text-green-800 mb-3 flex items-center gap-2 text-sm sm:text-base">
                  <ClipboardCheck className="w-4 h-4 sm:w-5 sm:h-5" />
                  Resumo da Homologação
                </h4>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 text-xs">
                  <div className="flex items-center gap-1">
                    {homologTestResults.confirm?.success ? <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-500" /> : <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4 text-gray-300" />}
                    <span className="truncate">Confirmar</span>
                  </div>
                  <div className="flex items-center gap-1">
                    {homologTestResults.cancel?.success ? <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-500" /> : <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4 text-gray-300" />}
                    <span className="truncate">Cancelar</span>
                  </div>
                  <div className="flex items-center gap-1">
                    {homologTestResults.ready?.success ? <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-500" /> : <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4 text-gray-300" />}
                    <span className="truncate">Pronto</span>
                  </div>
                  <div className="flex items-center gap-1">
                    {homologTestResults.dispatch?.success ? <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-500" /> : <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4 text-gray-300" />}
                    <span className="truncate">Dispatch</span>
                  </div>
                  <div className="flex items-center gap-1">
                    {homologTestResults.delivered?.success ? <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-500" /> : <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4 text-gray-300" />}
                    <span className="truncate">Entregue</span>
                  </div>
                  <div className="flex items-center gap-1">
                    {homologOrderDetails ? <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-500" /> : <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4 text-gray-300" />}
                    <span className="truncate">Dados</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Events Tab */}
        <TabsContent value="events">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Fila de Eventos</CardTitle>
                <CardDescription>
                  Eventos recebidos via webhook/polling
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={loadEvents} disabled={eventsLoading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${eventsLoading ? 'animate-spin' : ''}`} />
                Atualizar
              </Button>
            </CardHeader>
            <CardContent>
              {eventsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              ) : events.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Nenhum evento encontrado
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-3">Evento</th>
                        <th className="text-left py-2 px-3">Tipo</th>
                        <th className="text-left py-2 px-3">Pedido</th>
                        <th className="text-left py-2 px-3">Status</th>
                        <th className="text-left py-2 px-3">Tentativas</th>
                        <th className="text-left py-2 px-3">Recebido</th>
                        <th className="text-left py-2 px-3">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {events.map((event) => (
                        <tr key={event.id} className="border-b hover:bg-gray-50">
                          <td className="py-2 px-3 font-mono text-xs">{event.event_id}</td>
                          <td className="py-2 px-3">
                            <Badge variant="outline">{event.event_type}</Badge>
                          </td>
                          <td className="py-2 px-3 font-mono text-xs">{event.order_id}</td>
                          <td className="py-2 px-3">{getStatusBadge(event.status)}</td>
                          <td className="py-2 px-3">{event.tries}</td>
                          <td className="py-2 px-3 text-xs">
                            {new Date(event.received_at).toLocaleString('pt-BR')}
                          </td>
                          <td className="py-2 px-3">
                            {(event.status === 'FAILED' || event.status === 'DEAD') && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleRetryEvent(event.id)}
                              >
                                <RotateCcw className="w-4 h-4" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Orders Tab */}
        <TabsContent value="orders">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Pedidos 99Food</CardTitle>
                <CardDescription>
                  Pedidos recebidos do 99Food
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={loadOrders} disabled={ordersLoading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${ordersLoading ? 'animate-spin' : ''}`} />
                Atualizar
              </Button>
            </CardHeader>
            <CardContent>
              {ordersLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Nenhum pedido encontrado
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-3">ID</th>
                        <th className="text-left py-2 px-3">Display</th>
                        <th className="text-left py-2 px-3">Cliente</th>
                        <th className="text-left py-2 px-3">Total</th>
                        <th className="text-left py-2 px-3">Status</th>
                        <th className="text-left py-2 px-3">Pedido Local</th>
                        <th className="text-left py-2 px-3">Criado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((order) => (
                        <tr key={order.id} className="border-b hover:bg-gray-50">
                          <td className="py-2 px-3 font-mono text-xs">{order.order_id.substring(0, 12)}...</td>
                          <td className="py-2 px-3 font-bold">{order.display_id || '-'}</td>
                          <td className="py-2 px-3">{order.customer_name || '-'}</td>
                          <td className="py-2 px-3">R$ {order.total?.toFixed(2) || '0.00'}</td>
                          <td className="py-2 px-3">{getOrderStatusBadge(order.status)}</td>
                          <td className="py-2 px-3">
                            {order.local_order_id ? (
                              <Badge variant="outline" className="bg-green-50">Vinculado</Badge>
                            ) : (
                              <Badge variant="outline" className="bg-yellow-50">Não Vinculado</Badge>
                            )}
                          </td>
                          <td className="py-2 px-3 text-xs">
                            {new Date(order.created_at).toLocaleString('pt-BR')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Logs Tab */}
        <TabsContent value="logs">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Logs de API - Monitoramento</CardTitle>
                <CardDescription>
                  Visualize todos os JSONs enviados e recebidos da API 99Food
                </CardDescription>
              </div>
              <div className="flex gap-2 items-center">
                <select
                  value={logFilter.type}
                  onChange={(e) => setLogFilter({...logFilter, type: e.target.value})}
                  className="border rounded px-3 py-2 text-sm"
                >
                  <option value="">Todos</option>
                  <option value="MERCHANT">MERCHANT</option>
                  <option value="ORDER">ORDER</option>
                  <option value="EVENT">EVENT</option>
                  <option value="MENU">MENU</option>
                  <option value="AUTH">AUTH</option>
                </select>
                <Button variant="outline" size="sm" onClick={loadLogs} disabled={logsLoading}>
                  <RefreshCw className={`w-4 h-4 ${logsLoading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {logsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              ) : logs.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Nenhum log encontrado
                </div>
              ) : (
                <div className="divide-y">
                  {logs
                    .filter(log => {
                      if (logFilter.type && log.log_type !== logFilter.type) return false;
                      if (logFilter.status === 'success' && log.status_code >= 400) return false;
                      if (logFilter.status === 'error' && log.status_code < 400) return false;
                      return true;
                    })
                    .map((log) => (
                    <div key={log.id} className="p-4 hover:bg-gray-50">
                      {/* Header do Log */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className="font-mono">{log.log_type}</Badge>
                          <span className="font-mono text-sm font-medium">{log.method}</span>
                          <span className="font-mono text-sm text-gray-600">/{log.endpoint?.split('/').pop()}</span>
                          <Badge className={log.status_code < 400 ? 'bg-green-500' : 'bg-red-500'}>
                            {log.status_code}
                          </Badge>
                          <span className="text-sm text-gray-500">{log.response_time_ms}ms</span>
                        </div>
                        <span className="text-xs text-gray-400">
                          {new Date(log.created_at).toLocaleString('pt-BR')}
                        </span>
                      </div>
                      
                      {/* Request Body */}
                      {log.request_body && (
                        <div className="mb-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-semibold text-blue-600 uppercase">Request Body:</span>
                            <button 
                              onClick={() => navigator.clipboard.writeText(typeof log.request_body === 'string' ? log.request_body : JSON.stringify(log.request_body, null, 2))}
                              className="text-gray-400 hover:text-gray-600"
                              title="Copiar"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                          </div>
                          <pre className="bg-slate-900 text-green-400 p-4 rounded-lg text-xs overflow-x-auto max-h-64 font-mono">
                            {typeof log.request_body === 'string' 
                              ? log.request_body 
                              : JSON.stringify(log.request_body, null, 2)}
                          </pre>
                        </div>
                      )}
                      
                      {/* Response Body */}
                      {log.response_body && (
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-semibold text-purple-600 uppercase">Response Body:</span>
                            <button 
                              onClick={() => navigator.clipboard.writeText(typeof log.response_body === 'string' ? log.response_body : JSON.stringify(log.response_body, null, 2))}
                              className="text-gray-400 hover:text-gray-600"
                              title="Copiar"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                          </div>
                          <pre className={`p-4 rounded-lg text-xs overflow-x-auto max-h-64 font-mono ${log.status_code < 400 ? 'bg-slate-900 text-emerald-400' : 'bg-red-950 text-red-300'}`}>
                            {typeof log.response_body === 'string' 
                              ? log.response_body 
                              : JSON.stringify(log.response_body, null, 2)}
                          </pre>
                        </div>
                      )}
                      
                      {/* Erro */}
                      {log.error_message && (
                        <div className="mt-2 p-2 bg-red-50 rounded text-red-600 text-sm">
                          <span className="font-medium">Erro:</span> {log.error_message}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Diagnostic Tab */}
        <TabsContent value="diagnostic">
          <Didi99FoodDiagnostic connectionId={connectionId} />
        </TabsContent>
      </Tabs>
    </div>

    </MainLayout>

  );
};

export default Didi99FoodV4Page;
